/* eslint-disable */

import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import Loader from "react-loader-spinner";
import {
  PersonalInfo,
  LocationAndResume,
  ProfessionalDetails,
} from "components/formGroups";
import { getFormData, postForm } from "service/formService";
import { getJobDetails } from "service/formService";
import styles from "./Main.module.scss";
import { getDateString, useNavigatorOnLine, isReferredFromCareersPage } from "util/library";
import { RecruitmentNoticeModal } from "../RecruitmentNoticeModal/RecruitmentNoticeModal";
import PageLoader from "../Loader/Loader";

const PositionComponent = ({ jobPosting }) => {
  const position = jobPosting.position || null;
  return (
    jobPosting.position &&
    jobPosting.jobPostingId && (
      <div className={styles.jobPosting}>
        <p className={styles.jobTitle}>Applying For</p>
        <div className={styles.jobPositionRow}>
          <p className={styles.jobPosition}>{position}</p>
          <a
            href={`/current-openings/details?id=${jobPosting.jobPostingId}&position=${encodeURIComponent(
              position
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.jobDescriptionButton}
          >
            Job Description
          </a>
        </div>
      </div>
    )
  );
};

const Main = (props) => {
  // "checking" means id+position query params are present and are being validated
  // against the job postings API (same condition the fetch below acts on) before
  // deciding whether to skip the recruitment notice
  const [authStatus, setAuthStatus] = useState(() => {
    const params = new URLSearchParams(props.location.search);
    const id = params.get("id");
    const position = params.get("position");
    return id && position
      ? "checking"
      : isReferredFromCareersPage()
      ? "authorized"
      : "unauthorized";
  });
  const [state, setState] = useState({
    personalInfo: {
      fullName: "",
      emailaddress: "",
      phoneNumber: "",
      alternatePhoneNumber: "",
      linkedInProfile: "",
      dateOfBirth: "",
    },
    locationAndResume: {
      currentLocation: "",
      preferredJoiningCenter: "",
    },
    professionalDetails: {
      workExperience: "",
      tenthScore: "",
      twelvethScore: "",
      graduationStream: "",
      graduationStreamName: "",
      graduationScore: "",
      graduationCollegeOrUniversity: "",
      graduationYear: "",
      postGraduationStream: "",
      postGraduationStreamName: "",
      postGraduationScore: "",
      postGraduationCollegeOrUniversity: "",
      postGraduationYear: "",
      monthsOfExperience: "",
      yearsOfExperience: "",
      currentCompany: "",
      currentCompanyWebsite: "",
      currentCTC: "",
      firstSkill: "",
      secondSkill: "",
      thirdSkill: "",
      additionalSkills: "",
      noticePeriod: "",
      isCandidateOnNotice: "",
      lastWorkingDay: "",
    },
    selectedFile: null,
    candidateLocations: [],
    preferredLocations: [],
    streams: [],
    skills: [],
    noticePeriods: [],
    workExperiences: [],
    dateOfBirthError: false,
    workExperienceError: false,
    lastWorkingDayError: false,
    loading: true,
    fetchError: false,
  });
  const [jobPosting, setJobPosting] = useState({
    position: null,
    jobPostingId: null,
    minExp: null,
    maxExp: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const isOnline = useNavigatorOnLine();

  const history = useHistory();

  const formRef = useRef(null);
  useState(() => {
    let params = new URLSearchParams(props.location.search);
    let id = params.get("id"),
      position = params.get("position");

    if (id !== null && position !== null) {
      if (id.length > 0 && position.length > 0) {
        getJobDetails(id)
          .then((response) => {
            setJobPosting({
              minExp: response.data.minExp,
              maxExp: response.data.maxExp,
              position: response.data.position,
              jobPostingId: response.data.jobPostingId,
            });
            // id maps to a real, current job posting - skip the recruitment notice
            setAuthStatus("authorized");
          })
          .catch((err) => {
            // id didn't validate against a real posting - fall back to the normal check
            setAuthStatus(
              isReferredFromCareersPage() ? "authorized" : "unauthorized"
            );

            if (err.response.status.toString().startsWith("4")) {
              // Client error, redirect to job listings
              history.push("/");
              return;
            }
          });

        return;
      }
      // id or position is empty
      if (id.length === 0 || position.length === 0) {
        history.push("/");
        return;
      }

      // id and position are both empty strings
      if (id.length <= 0 && position.length <= 0) {
        history.push("/");
        return;
      }
    }

    // Job detail can be passed through location state and query string
  }, [props.location.search, jobPosting]);

  useEffect(() => {
    if (authStatus === "unauthorized") {
      return;
    }

    getFormData()
      .then((response) => {
        const {
          candidateLocations,
          gradStreams,
          preferredLocations,
          skills,
          noticePeriods,
          workExperiences,
        } = response.data;

        setState({
          ...state,
          candidateLocations,
          preferredLocations,
          streams: gradStreams,
          skills,
          noticePeriods,
          workExperiences,
          loading: false,
          fetchError: false,
        });
      })
      .catch(() => {
        setState({
          ...state,
          loading: false,
          fetchError: true,
        });
      });
  }, []);

  // change handlers
  const handleChange = (subObjectKey, name, value) => {
    setState({
      ...state,
      [subObjectKey]: {
        ...state[subObjectKey],
        [name]: value,
      },
    });
  };

  const handleFileChange = (selectedFile) => {
    setState({
      ...state,
      selectedFile,
    });
  };

  const handlePersonalInfoChange = ({ target: { name, value } }) =>
    handleChange("personalInfo", name, value);

  const handleLocationAndResumeChange = ({ target: { name, value } }) =>
    handleChange("locationAndResume", name, value);

  const handleProfessionalDetailsChange = ({ target: { name, value } }) => {
    setState({
      ...state,
      professionalDetails: {
        ...state.professionalDetails,
        [name]: value,
      },
      workExperienceError: false,
      lastWorkingDayError: false,
    });
  };

  const handleGradStreamChange = (streamValue, streamName = "") => {
    setState({
      ...state,
      professionalDetails: {
        ...state.professionalDetails,
        graduationStream: streamValue,
        graduationStreamName: streamName,
      },
      workExperienceError: false,
    });
  };

  const handlePostGradStreamChange = (streamValue, streamName = "") => {
    setState({
      ...state,
      professionalDetails: {
        ...state.professionalDetails,
        postGraduationStream: streamValue,
        postGraduationStreamName: streamName,
      },
      workExperienceError: false,
    });
  };

  const handleDateChange = (date) => {
    setState({
      ...state,
      personalInfo: {
        ...state.personalInfo,
        dateOfBirth: date,
      },
      dateOfBirthError: false,
    });
  };
  const handleLastWorkingDayChange = (date) => {
    setState({
      ...state,
      professionalDetails: {
        ...state.professionalDetails,
        lastWorkingDay: date,
      },
      lastWorkingDayError: false,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isOnline) {
      let firstInvalidElement;
      const invalidElements = formRef.current.querySelectorAll(":invalid");

      if (invalidElements.length !== 0) {
        if (invalidElements[0].type === "file") {
          // file input is valid, so picking the next element if any as the firstInvalidElement
          if (state.selectedFile) {
            firstInvalidElement =
              invalidElements.length >= 2 ? invalidElements[1] : null;
          } else {
            firstInvalidElement = document.querySelector(
              "#fileUploadContainer"
            );
          }
        } else {
          firstInvalidElement = invalidElements[0];
        }
      }

      if (firstInvalidElement) {
        // adding class to form to enable invalid input styling
        formRef.current.classList.add("form-invalid");

        const navbarHeight = 100;
        // const elementOffset = firstInvalidElement.offsetTop - navbarHeight;
        // const pageOffset = window.pageYOffset - navbarHeight;

        // scrolling only if the element is currently not in view
        // if (
        //   !(
        //     elementOffset > pageOffset &&
        //     elementOffset < pageOffset + window.innerHeight
        //   )
        // ) {
        //   window.scrollTo({
        //     top: elementOffset,
        //     behavior: "smooth",
        //   });
        // }
        firstInvalidElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        // setting dobError based on whether a date is selected on not
        // scoped to #dob specifically - .react-datepicker-wrapper also wraps
        // lastWorkingDay's picker, and a generic query would misattribute
        // that field's invalid state to Date of Birth
        const dobError = document.querySelector("#dob:invalid") ? true : false;

        // settings workExperienceError based on whether an option is selected or not
        const workExperienceError = document.querySelector(
          ".form-invalid .work-experience :invalid"
        )
          ? true
          : false;

        // lastWorkingDay is only actually required (and thus only ever :invalid)
        // when isCandidateOnNotice is "true" - see ProfessionalDetails.js
        const lastWorkingDayError = document.querySelector(
          "#lastWorkingDay:invalid"
        )
          ? true
          : false;

        setState({
          ...state,
          dateOfBirthError: dobError,
          workExperienceError,
          lastWorkingDayError,
        });
      }

      // there are no invalid elements, so we can submit the form
      else {
        setIsSubmitting(true);

        window.grecaptcha.ready(function () {
          window.grecaptcha
            .execute(process.env.REACT_APP_SITE_KEY, { action: "submit" })
            .then(function (RecaptchaToken) {
              const userDetails = {
                ...state.personalInfo,
                ...state.locationAndResume,
                ...state.professionalDetails,
                dateOfBirth: getDateString(state.personalInfo.dateOfBirth),
                lastWorkingDay:   getDateString(
                  state.professionalDetails.lastWorkingDay
                ),
                RecaptchaToken,
                Position:
                  jobPosting.position === null ? "" : jobPosting.position,
                JobId: jobPosting.jobPostingId ?? 0,
              };

              const formData = new FormData();

              // appending all field data
              for (const key of Object.keys(userDetails)) {
                formData.append(key, userDetails[key]);
              }

              // appending resume file
              formData.append("resume", state.selectedFile);
              postForm(formData)
                .then(function redirectToSuccessPage() {
                  history.push("/success");
                })
                .catch((error) => {
                  const status = Number(error.response.status);
                  const errorMessage =
                    status === 422
                      ? error.response.data["RecaptchToken"]
                        ? error.response.data["RecaptchToken"]
                        : ""
                      : error.response.data[0];

                  setIsSubmitting(false);
                  setSubmitErrorMessage(errorMessage);
                });
            });
        });
      }
    } else {
      setShowOfflineMessage(true);
    }
  };
  if (authStatus === "checking") {
    return (
      <div className={styles.loaderContainer}>
        <PageLoader />
      </div>
    );
  }

  if (authStatus === "unauthorized") {
    return <RecruitmentNoticeModal />;
  }

  return state.fetchError ? (
    <div className={styles.errorContainer}>
      <h1 className={styles.message}>
        Something went wrong!!! Please refresh the page or try again later
      </h1>
    </div>
  ) : (
    <main className={styles.main}>
      <form
        className={styles.form}
        onSubmit={handleSubmit}
        ref={formRef}
        noValidate
      >
        {jobPosting !== null &&
          (props.location.state || props.location.search) && (
            <PositionComponent jobPosting={jobPosting} />
          )}
        <PersonalInfo
          handleChange={handlePersonalInfoChange}
          handleDateChange={handleDateChange}
          dateOfBirthError={state.dateOfBirthError}
          {...state.personalInfo}
          setSubmitErrorMessage={setSubmitErrorMessage}
        />
        <LocationAndResume
          handleChange={handleLocationAndResumeChange}
          candidateLocations={state.candidateLocations}
          preferredLocations={state.preferredLocations}
          handleFileChange={handleFileChange}
          submitErrorMessage={submitErrorMessage}
          {...state.locationAndResume}
        />
        <ProfessionalDetails
          {...state.professionalDetails}
          handleChange={handleProfessionalDetailsChange}
          handleGradStreamChange={handleGradStreamChange}
          handlePostGradStreamChange={handlePostGradStreamChange}
          handleLastWorkingDayChange={handleLastWorkingDayChange}
          graduationStreams={state.streams}
          state={state}
          setState={setState}
          postGraduationStreams={state.streams}
          skills={state.skills}
          jobPosting={jobPosting}
          workExperiences={state.workExperiences}
          workExperienceError={state.workExperienceError}
          lastWorkingDayError={state.lastWorkingDayError}
          noticePeriods={state.noticePeriods}
        />
        <div className={styles.actionContainer}>
          {submitErrorMessage && (
            <p className={styles.submitErrorMessage}>{submitErrorMessage}</p>
          )}
          {!isOnline && showOfflineMessage && (
            <p className={styles.submitErrorMessage}>Unable to submit.</p>
          )}

          <button
            className={styles.button}
            disabled={isSubmitting || submitErrorMessage}
            type="submit"
          >
            {isSubmitting && (
              <Loader
                className={styles.loading}
                type="TailSpin"
                color="#F4EDD5"
                height={20}
                width={20}
              />
            )}
            Submit
          </button>
        </div>
      </form>
    </main>
  );
};

export { Main };
